// Stage D — bulk add-to-cart custom element for the color chip grid.
// Tracks per-chip quantities, posts a single bulk add to /cart/add.js,
// then refreshes Dawn's cart drawer/notification using the standard
// sections-rendering response.

class ColorGridCart extends HTMLElement {
  constructor() {
    super();
    this.qtys = new Map();
  }

  connectedCallback() {
    this.bulkButton = this.querySelector('[data-cgp-bulk-add]');
    this.countWrapper = this.querySelector('[data-cgp-cart-count]');
    this.countValue = this.querySelector('[data-cgp-cart-count-value]');
    this.spinner = this.querySelector('[data-cgp-spinner]');
    this.errorEl = this.querySelector('[data-cgp-error]');
    this.selectionPanel = this.querySelector('[data-cgp-selection]');
    this.selectionList = this.querySelector('[data-cgp-selection-list]');
    this.selectionSubtotal = this.querySelector('[data-cgp-subtotal]');
    this.footer = this.querySelector('.cgp__footer');
    this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

    // Localized strings (set as data attributes by the Liquid snippet so the
    // theme's translation file is the source of truth).
    this.i18n = {
      errorCartAdd: this.dataset.errorCartAdd || 'Something went wrong adding to cart.',
      errorNetwork: this.dataset.errorNetwork || 'Network error. Please try again.',
      removeAriaTemplate: this.dataset.removeAriaTemplate || 'Remove __CODE__',
    };

    // The .product__info-wrapper carries .scroll-trigger.animate--slide-in
    // which applies a CSS transform. A transformed ancestor breaks position:
    // fixed on descendants. Move the footer to document.body so the floating
    // bar is positioned relative to the viewport instead.
    if (this.footer && this.footer.parentElement !== document.body) {
      document.body.appendChild(this.footer);
    }

    this.addEventListener('click', this.onChipClick.bind(this));
    if (this.footer) {
      this.footer.addEventListener('click', this.onFooterClick.bind(this));
    }

    this.gridEl = this.querySelector('.cgp__grid');
    this.alignFooter = this.alignFooter.bind(this);
    this.alignFooter();
    window.addEventListener('resize', this.alignFooter);

    // Hide the floating bar when the chip grid scrolls out of view (user is
    // now past the picker, e.g., in 'You may also like'). Reappears when the
    // grid is back in view. Uses style.display directly to avoid any CSS
    // transition timing issues that would mis-size the bar on initial load.
    if (this.gridEl && this.footer && 'IntersectionObserver' in window) {
      this.gridObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            // Guard: don't act until the grid has actually been laid out.
            const r = entry.target.getBoundingClientRect();
            if (r.height === 0) return;

            if (entry.isIntersecting) {
              this.footer.style.display = '';
              this.alignFooter();
            } else {
              this.footer.style.display = 'none';
            }
          });
        },
        { threshold: 0 }
      );
      this.gridObserver.observe(this.gridEl);
    }
  }

  disconnectedCallback() {
    if (this.footer && this.footer.parentElement === document.body) {
      this.footer.remove();
    }
    window.removeEventListener('resize', this.alignFooter);
    if (this.gridObserver) {
      this.gridObserver.disconnect();
    }
  }

  alignFooter() {
    if (!this.footer || !this.gridEl) return;

    // Mobile: full-width bar (CSS handles it). Clear any inline overrides.
    if (window.innerWidth < 750) {
      this.footer.style.left = '';
      this.footer.style.width = '';
      return;
    }

    const rect = this.gridEl.getBoundingClientRect();
    this.footer.style.left = `${Math.round(rect.left)}px`;
    this.footer.style.width = `${Math.round(rect.width)}px`;
  }

  onChipClick(evt) {
    const stepperBtn = evt.target.closest('[data-cgp-action]');
    if (stepperBtn) {
      this.handleStepper(stepperBtn);
    }
  }

  onFooterClick(evt) {
    const removeBtn = evt.target.closest('[data-cgp-selection-remove]');
    if (removeBtn) {
      evt.preventDefault();
      this.removeFromSelection(removeBtn.dataset.cgpSelectionRemove);
      return;
    }
    if (evt.target.closest('[data-cgp-bulk-add]')) {
      evt.preventDefault();
      this.submitBulkAdd();
    }
  }

  removeFromSelection(variantId) {
    if (!variantId) return;
    this.qtys.delete(variantId);
    const chip = this.querySelector(`.cgp-chip[data-variant-id="${CSS.escape(variantId)}"]`);
    if (chip) {
      chip.classList.remove('is-selected');
      const qtyEl = chip.querySelector('[data-cgp-qty]');
      if (qtyEl) qtyEl.textContent = '0';
    }
    this.updateBulkButton();
    this.renderSelectionList();
  }

  handleStepper(btn) {
    const chip = btn.closest('.cgp-chip');
    if (!chip || chip.classList.contains('is-unavailable')) return;

    const variantId = chip.dataset.variantId;
    const action = btn.dataset.cgpAction;
    const current = this.qtys.get(variantId) || 0;
    const next = action === 'increment' ? current + 1 : Math.max(0, current - 1);

    if (next === current) return;

    if (next === 0) {
      this.qtys.delete(variantId);
    } else {
      this.qtys.set(variantId, next);
    }

    const qtyEl = chip.querySelector('[data-cgp-qty]');
    if (qtyEl) qtyEl.textContent = String(next);
    chip.classList.toggle('is-selected', next > 0);

    this.updateBulkButton();
    this.renderSelectionList();
  }

  updateBulkButton() {
    const totalItems = Array.from(this.qtys.values()).reduce((sum, q) => sum + q, 0);
    const hasAny = totalItems > 0;
    this.bulkButton.disabled = !hasAny;
    if (this.countWrapper) {
      this.countWrapper.hidden = !hasAny;
      if (this.countValue) this.countValue.textContent = String(totalItems);
    }
  }

  renderSelectionList() {
    if (!this.selectionPanel || !this.selectionList) return;

    const entries = [];
    let subtotalCents = 0;
    let priceFormatter = null;

    this.qtys.forEach((qty, variantId) => {
      if (qty <= 0) return;
      const chip = this.querySelector(`.cgp-chip[data-variant-id="${CSS.escape(variantId)}"]`);
      if (!chip) return;
      const code = chip.dataset.variantCode || '';
      const priceCents = Number(chip.dataset.variantPrice || 0);
      const priceFormatted = chip.dataset.variantPriceFormatted || '';
      // Pull the swatch element so we can clone its computed background
      const swatchEl = chip.querySelector('.cgp-chip__swatch');
      const swatchStyle = swatchEl ? swatchEl.getAttribute('style') || '' : '';
      subtotalCents += priceCents * qty;
      if (!priceFormatter && priceFormatted) priceFormatter = priceFormatted;
      entries.push({ variantId, qty, code, priceFormatted, swatchStyle });
    });

    if (entries.length === 0) {
      this.selectionPanel.hidden = true;
      this.selectionList.innerHTML = '';
      if (this.selectionSubtotal) this.selectionSubtotal.textContent = '';
      return;
    }

    this.selectionPanel.hidden = false;

    this.selectionList.innerHTML = entries
      .map((e) => `
        <li class="cgp__selection-item" data-cgp-selection-item data-variant-id="${e.variantId}">
          <span class="cgp__selection-swatch" style="${e.swatchStyle}"></span>
          <span class="cgp__selection-code">${escapeHtml(e.code)}</span>
          <span class="cgp__selection-qty">×${e.qty}</span>
          <span class="cgp__selection-line-price">${escapeHtml(e.priceFormatted)}</span>
          <button
            type="button"
            class="cgp__selection-remove"
            data-cgp-selection-remove="${e.variantId}"
            aria-label="${escapeHtml(this.i18n.removeAriaTemplate.replace('__CODE__', e.code))}"
          >×</button>
        </li>
      `)
      .join('');

    if (this.selectionSubtotal) {
      // Approximate subtotal by reusing the formatting of the first price (locale + currency).
      // For exact formatting we'd need a server round-trip; this is good enough for an inline preview.
      const sample = entries[0].priceFormatted;
      const sampleNumber = (Number(this.querySelector(`.cgp-chip[data-variant-id="${CSS.escape(entries[0].variantId)}"]`)?.dataset.variantPrice) || 0);
      const formatted = sampleNumber > 0
        ? sample.replace(/[\d.,]+/, (match) => {
            const decimalIsComma = /\d,\d{1,2}(?!\d)/.test(sample);
            const value = subtotalCents / 100;
            return decimalIsComma
              ? value.toFixed(2).replace('.', ',')
              : value.toFixed(2);
          })
        : '';
      this.selectionSubtotal.textContent = formatted;
    }
  }

  buildItems() {
    return Array.from(this.qtys.entries())
      .filter(([id, qty]) => id && qty > 0)
      .map(([id, qty]) => ({ id: Number(id), quantity: qty }));
  }

  async submitBulkAdd() {
    const items = this.buildItems();
    if (!items.length) return;

    this.setBusy(true);
    this.hideError();

    const body = { items };
    if (this.cart && typeof this.cart.getSectionsToRender === 'function') {
      body.sections = this.cart.getSectionsToRender().map((s) => s.id);
      body.sections_url = window.location.pathname;
      if (typeof this.cart.setActiveElement === 'function') {
        this.cart.setActiveElement(document.activeElement);
      }
    }

    try {
      const response = await fetch(window.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify(body),
      });

      const data = await response.json();

      if (!response.ok || data.status) {
        const msg = data.description || data.message || this.i18n.errorCartAdd;
        this.showError(msg);
        return;
      }

      // Notify Dawn's pub/sub so cart drawer + headers update.
      if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS) {
        window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
          source: 'color-grid-picker',
          cartData: data,
        });
      }

      if (this.cart && typeof this.cart.renderContents === 'function') {
        this.cart.renderContents(data);
        if (this.cart.classList && this.cart.classList.contains('is-empty')) {
          this.cart.classList.remove('is-empty');
        }
      } else {
        // No cart drawer/notification present — fall back to the cart page.
        window.location = window.routes.cart_url || '/cart';
        return;
      }

      this.resetSelection();
    } catch (err) {
      console.error('[color-grid-picker] bulk add failed', err);
      this.showError(this.i18n.errorNetwork);
    } finally {
      this.setBusy(false);
    }
  }

  setBusy(busy) {
    this.bulkButton.classList.toggle('is-loading', busy);
    if (this.spinner) this.spinner.hidden = !busy;
    this.bulkButton.setAttribute('aria-busy', busy ? 'true' : 'false');
  }

  showError(msg) {
    if (!this.errorEl) return;
    this.errorEl.textContent = msg;
    this.errorEl.hidden = false;
  }

  hideError() {
    if (!this.errorEl) return;
    this.errorEl.hidden = true;
    this.errorEl.textContent = '';
  }

  resetSelection() {
    this.qtys.clear();
    this.querySelectorAll('.cgp-chip').forEach((chip) => {
      chip.classList.remove('is-selected');
      const qtyEl = chip.querySelector('[data-cgp-qty]');
      if (qtyEl) qtyEl.textContent = '0';
    });
    this.updateBulkButton();
    this.renderSelectionList();
  }
}

function escapeHtml(str) {
  return String(str).replace(/[&<>"']/g, (ch) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
  }[ch]));
}

if (!customElements.get('color-grid-cart')) {
  customElements.define('color-grid-cart', ColorGridCart);
}
